// Frontend wrappers for invoice-related server work.
//
// PDF rendering moved from a Puppeteer Cloud Function to the `invoice-pdf`
// edge function. Deno Deploy has no headless Chrome, so the renderer builds
// the PDF directly rather than screenshotting HTML — see the function itself.

import { supabase } from '@/lib/supabase';

interface GenerateInvoicePdfResponse {
  success: boolean;
  size: number;
  pdfBase64: string;
  invoiceNumber: string;
}

/** Trigger the Cloud Function to regenerate an invoice PDF and return base64. */
export async function regenerateInvoicePdf(
  transactionId: string,
): Promise<GenerateInvoicePdfResponse> {
  const { data, error } = await supabase.functions.invoke<GenerateInvoicePdfResponse>(
    'invoice-pdf',
    { body: { transactionId } },
  );
  if (error) throw new Error(error.message);
  if (!data) throw new Error('PDF kosong');
  return data;
}

/** Re-send the invoice over WhatsApp using whichever gateway is enabled. */
export async function resendWhatsApp(
  transactionId: string,
): Promise<{ status: string; gateway?: string; error?: string }> {
  const { data, error } = await supabase.functions.invoke<{
    status: string; gateway?: string; error?: string;
  }>('whatsapp-dispatch', { body: { transactionId } });
  if (error) throw new Error(error.message);
  return data ?? { status: 'unknown' };
}

/** Convert base64 to a Blob for download. */
export function base64ToBlob(base64: string, mimeType = 'application/pdf'): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

/** Trigger browser download of a Blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export interface ShareInvoiceResult {
  ok: boolean;
  method: 'native' | 'download' | 'cancelled' | 'unsupported';
}

/**
 * Share/download the PDF using the Web Share API where supported.
 * Falls back to a direct download.
 */
export async function shareInvoicePdf(
  blob: Blob,
  filename: string,
  title: string,
  text: string,
): Promise<ShareInvoiceResult> {
  const nav = navigator as Navigator & {
    canShare?: (data: ShareData) => boolean;
    share?: (data: ShareData) => Promise<void>;
  };
  const file = new File([blob], filename, { type: 'application/pdf' });
  if (typeof nav.canShare === 'function' && nav.canShare({ files: [file] })) {
    try {
      await nav.share!({ files: [file], title, text });
      return { ok: true, method: 'native' };
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') {
        return { ok: false, method: 'cancelled' };
      }
      // fall through to download
    }
  }
  downloadBlob(blob, filename);
  return { ok: true, method: 'download' };
}

